import { buffer } from "@turf/buffer"
import { decodePolyline } from "@/lib/utils"
import { samplePath } from "@/lib/route-denormalize"
import { cityStopPointOnPath, CITY_STOP_WINDOW_KM, cumulativeKmAlong } from "@/lib/city-stop-point"
import { type LatLng, haversineKm, pathTotalKm, projectOnPathKm } from "@/lib/route-matching"
import type { RouteCityCandidate } from "@/lib/route-city-suggestions"
import type { Db } from "mongodb"

export const CORRIDOR_RADIUS_KM = 18
export const CORRIDOR_MAX_SAMPLE_POINTS = 40
export const MIN_DISTANCE_FROM_ENDPOINTS_KM = 3
export const MAX_MUNICIPALITIES = 200

// Política do corredor/cidade de passagem (ver docs/ARCHITECTURE.md):
// - trim = 5% da rota (piso 18, teto 18): o corredor "anda" — só é ativo no
//   trecho central; o início e a cauda da rota não geram sugestões.
// - Raio por porte em DEGRAUS (cliff por faixa de população), nunca linear:
//   pequeno vive perto do eixo, capital/gigante alcança o limite do corredor.
//   Capital de UF ou porte gigante entra mesmo "distante" (raio 40 fixo,
//   ignora o teto proporcional — só um corredor de 40 km limita).
export const RADIUS_MAX_KM = 40
export const RADIUS_TIER_GIANT_POP = 300_000
export const RADIUS_TIER_LARGE_POP = 100_000
export const RADIUS_TIER_MID_POP = 30_000
export const CORRIDOR_WINDOW_TOLERANCE_KM = 2
export const MIN_POP_SUGGESTED = 8_000
export const MAX_OFF_ROUTE_KM_SMALL_VILLAGE = 4

export function centerCapRadiusKm(totalKm: number): number {
  return Math.min(RADIUS_MAX_KM, Math.max(CORRIDOR_RADIUS_KM, totalKm * 0.12))
}

export function corridorTrimKm(totalKm: number): number {
  return Math.min(RADIUS_MAX_KM, Math.max(CORRIDOR_RADIUS_KM, totalKm * 0.05))
}

// Raio por porte em degraus (política "cliff", ver docs/ARCHITECTURE.md):
//   ≥ 300.000 hab  → 40 km (gigante/capital)
//   100.000–299.999 → 18 km
//   30.000–99.999  →  6 km
//   < 30.000       →  3 km
export function communityRadiusKm(population: number): number {
  const pop = Number.isFinite(population) ? population : 0
  if (pop >= RADIUS_TIER_GIANT_POP) return RADIUS_MAX_KM
  if (pop >= RADIUS_TIER_LARGE_POP) return 18
  if (pop >= RADIUS_TIER_MID_POP) return 6
  return 3
}

// Raio efetivo: capital de UF ou porte gigante entra mesmo distante (raio 40
// fixo, isento do teto proporcional); as demais respeitam o degrau de porte,
// limitado ainda pelo teto proporcional ao corredor.
export function effectiveRadiusKm(population: number, totalKm: number, isCapital = false): number {
  if (isCapital || population >= RADIUS_TIER_GIANT_POP) return RADIUS_MAX_KM
  return Math.min(communityRadiusKm(population), centerCapRadiusKm(totalKm))
}

function lerpPoint(a: LatLng, b: LatLng, t: number): LatLng {
  return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t }
}

function cumulativeAlongKm(points: LatLng[]): number[] {
  const along: number[] = [0]
  for (let i = 0; i < points.length - 1; i++) {
    along.push(along[i] + haversineKm(points[i].lat, points[i].lng, points[i + 1].lat, points[i + 1].lng))
  }
  return along
}

// Recorta o trecho central da rota: [trimKm, totalKm - trimKm]. Interpola os
// pontos de entrada/saída nos segmentos que cruzam as bordas, preservando um
// corredor válido mesmo para rotas de 2 pontos (sem polyline).
export function trimPathToWindow(points: LatLng[], trimKm: number, totalKm: number): LatLng[] {
  if (points.length < 2) return []
  if (totalKm <= 2 * trimKm) return []
  const along = cumulativeAlongKm(points)
  const start = trimKm
  const end = totalKm - trimKm

  function crossing(target: number): number {
    for (let i = 0; i < along.length - 1; i++) {
      if (along[i + 1] >= target) return i
    }
    return along.length - 2
  }

  const startSeg = crossing(start)
  const endSeg = crossing(end)
  const segLenStart = along[startSeg + 1] - along[startSeg]
  const segLenEnd = along[endSeg + 1] - along[endSeg]
  const tStart = segLenStart > 0 ? (start - along[startSeg]) / segLenStart : 0
  const tEnd = segLenEnd > 0 ? (end - along[endSeg]) / segLenEnd : 1
  const entry = lerpPoint(points[startSeg], points[startSeg + 1], Math.min(1, Math.max(0, tStart)))
  const exit = lerpPoint(points[endSeg], points[endSeg + 1], Math.min(1, Math.max(0, tEnd)))
  const inner = points.slice(startSeg + 1, endSeg + 1)
  return [entry, ...inner, exit]
}

export interface MunicipalityDoc {
  code: string
  nome: string
  uf: string
  populacao: number
  isCapital: boolean
  isMunicipalSeat: boolean
  centroid: { type: "Point"; coordinates: [number, number] }
  geometry: { type: "Polygon" | "MultiPolygon"; coordinates: number[][][] | number[][][][] }
}

export type CorridorGeometry = { type: "Polygon" | "MultiPolygon"; coordinates: number[][][] | number[][][][] }

export function pointInRing(point: [number, number], ring: number[][]): boolean {
  const [px, py] = point
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0]
    const yi = ring[i][1]
    const xj = ring[j][0]
    const yj = ring[j][1]
    const intersects = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi
    if (intersects) inside = !inside
  }
  return inside
}

export function pointInPolygon(point: [number, number], polygon: number[][][]): boolean {
  const outer = polygon[0]
  if (!outer) return false
  return pointInRing(point, outer)
}

export function pointInMultiPolygon(point: [number, number], multiPolygon: number[][][][]): boolean {
  return multiPolygon.some((polygon) => pointInPolygon(point, polygon))
}

export function pointInMunicipality(
  point: [number, number],
  geometry: MunicipalityDoc["geometry"]
): boolean {
  if (geometry.type === "Polygon") return pointInPolygon(point, geometry.coordinates as number[][][])
  return pointInMultiPolygon(point, geometry.coordinates as number[][][][])
}

// Parâmetro de projeção sobre o segmento a→b sem clamp. Negativo = antes de `a`
// (antes do início), >1 = depois de `b` (depois do fim). Usado para recortar o
// corredor nos extremos da rota, já que projectOnPathKm fixa alongKm em [0, totalKm].
export function segmentProjectionT(point: LatLng, a: LatLng, b: LatLng): number {
  const dx = b.lat - a.lat
  const dy = b.lng - a.lng
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return 0
  return ((point.lat - a.lat) * dx + (point.lng - a.lng) * dy) / lenSq
}

export function pathFromPolyline(polyline: string | null | undefined): LatLng[] {
  if (!polyline) return []
  try {
    return decodePolyline(polyline)
  } catch {
    return []
  }
}

export function bufferCorridorPolygon(
  path: LatLng[],
  radiusKm: number = CORRIDOR_RADIUS_KM,
  trimKm = 0
): CorridorGeometry | null {
  if (path.length < 2) return null
  const sampled = path.length > CORRIDOR_MAX_SAMPLE_POINTS ? samplePath(path, CORRIDOR_MAX_SAMPLE_POINTS) : path
  if (sampled.length < 2) return null

  let points = sampled
  if (trimKm > 0) {
    const totalKm = pathTotalKm(path)
    points = trimPathToWindow(sampled, trimKm, totalKm)
    if (points.length < 2) return null
  }

  const line = {
    type: "LineString" as const,
    coordinates: points.map((p) => [p.lng, p.lat] as [number, number]),
  }
  try {
    const buffered = buffer(line, radiusKm, { units: "kilometers" })
    if (!buffered || !buffered.geometry) return null
    const geom = buffered.geometry
    if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
      return { type: geom.type, coordinates: geom.coordinates }
    }
    return null
  } catch {
    return null
  }
}

export interface FindMunicipalitiesOptions {
  radiusKm?: number
  collection?: "ibge_municipalities"
}

/**
 * Município que CONTÉM um ponto.
 *
 * É o contexto que falta para a cidade digitada à mão: sem `cityCode`/`cityName`
 * a parada não pode ser refinada (a busca de lugares exige o código IBGE) nem
 * entra na captura por cidade. `$geoIntersects` com Point usa o índice 2dsphere
 * de `geometry`; em raro sobreposição de polígonos (divisa simplificada) vence
 * o município mais populoso — mesmo desempate do corredor.
 *
 * Devolve `null` para coordenada inválida, coleção indisponível ou ponto fora
 * de qualquer município (costa, fronteira) — quem chama decide o fallback.
 */
export async function findMunicipalityAtPoint(
  db: Db,
  lat: number,
  lng: number,
  opts: FindMunicipalitiesOptions = {}
): Promise<MunicipalityDoc | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
  try {
    return await db
      .collection<MunicipalityDoc>(opts.collection ?? "ibge_municipalities")
      .findOne(
        { geometry: { $geoIntersects: { $geometry: { type: "Point", coordinates: [lng, lat] } } } },
        { sort: { populacao: -1 } }
      )
  } catch {
    return null
  }
}

export async function findMunicipalitiesAlongPath(
  path: LatLng[],
  db: Db,
  opts: FindMunicipalitiesOptions = {}
): Promise<RouteCityCandidate[]> {
  if (path.length < 2) return []

  const totalKm = pathTotalKm(path)
  if (!(totalKm > 0)) return []

  const trimKm = corridorTrimKm(totalKm)
  if (totalKm <= 2 * trimKm + CORRIDOR_WINDOW_TOLERANCE_KM) return []

  // O corredor "anda": só o trecho central [trimKm, totalKm - trimKm] é
  // consultado. Bufferm com o raio máximo (centerCap) para capturar também os
  // grandes centros que entram com raio estendido (filtro por raio efetivo é
  // feito em memória por município).
  const bufferRadius = opts.radiusKm ?? centerCapRadiusKm(totalKm)
  const corridor = bufferCorridorPolygon(path, bufferRadius, trimKm)
  if (!corridor) return []

  let docs: MunicipalityDoc[]
  try {
    docs = await db
      .collection<MunicipalityDoc>(opts.collection ?? "ibge_municipalities")
      .find({ geometry: { $geoIntersects: { $geometry: corridor } } })
      .sort({ populacao: -1 })
      .limit(MAX_MUNICIPALITIES)
      .toArray()
  } catch {
    return []
  }

  const origin = path[0]
  const destination = path[path.length - 1]
  // Calculado uma vez e reaproveitado por todos os candidatos: sem isso cada
  // município pagaria um O(n) sobre o polyline full-res.
  const pathAlongKm = cumulativeKmAlong(path)

  const candidates: RouteCityCandidate[] = []
  for (const doc of docs) {
    const coord = doc.centroid?.coordinates
    if (!Array.isArray(coord) || coord.length < 2) continue
    const lat = coord[1]
    const lng = coord[0]

    // Município que contém origem ou destino nunca é "cidade de passagem".
    if (pointInMunicipality([origin.lng, origin.lat], doc.geometry)) continue
    if (pointInMunicipality([destination.lng, destination.lat], doc.geometry)) continue

    // Corredor não se estende antes do início nem depois do fim da rota.
    const firstT = segmentProjectionT({ lat, lng }, path[0], path[1])
    const lastT = segmentProjectionT(
      { lat, lng },
      path[path.length - 2],
      path[path.length - 1]
    )
    if (firstT < 0 || lastT > 1) continue

    if (haversineKm(origin.lat, origin.lng, lat, lng) < MIN_DISTANCE_FROM_ENDPOINTS_KM) continue
    if (haversineKm(destination.lat, destination.lng, lat, lng) < MIN_DISTANCE_FROM_ENDPOINTS_KM) continue

    const projection = projectOnPathKm({ lat, lng }, path)
    if (!projection) continue

    const alongKm = projection.alongKm

    // Janela central: cidade de passagem só dentro de [trim, total - trim]
    // (o corredor "anda": nada nos primeiros/últimos trimKm).
    if (alongKm < trimKm - CORRIDOR_WINDOW_TOLERANCE_KM) continue
    if (alongKm > totalKm - trimKm + CORRIDOR_WINDOW_TOLERANCE_KM) continue

    const population = doc.populacao || 0

    // Cortina de lixo: município minúsculo E fora do eixo não é sugestão.
    if (population < MIN_POP_SUGGESTED && projection.distanceKm > MAX_OFF_ROUTE_KM_SMALL_VILLAGE) continue

    // Raio efetivo por porte (linear) com teto proporcional à rota.
    // Raio efetivo por porte (degrau) — capital de UF entra mesmo distante
    // (raio 40 fixo, isento do teto proporcional).
    const radius = effectiveRadiusKm(population, totalKm, !!doc.isCapital)
    if (projection.distanceKm > radius) continue

    // Ponto de parada ancorado NA ROTA, dentro do município. É o que o front
    // usa como pin e como waypoint do OSRM — o centroide (lat/lng acima) ficava
    // a dezenas de km do eixo e fazia a rota fazer um desvio até o centro da
    // cidade. `null` quando a rota não chega perto o bastante: aí o chamador
    // mantém o centroide em vez de inventar um ponto do lado errado.
    const stopPoint = cityStopPointOnPath(
      { lat, lng },
      doc.geometry,
      path,
      { windowKm: CITY_STOP_WINDOW_KM, centerAlongKm: alongKm, along: pathAlongKm }
    )

    candidates.push({
      name: `${doc.nome}, ${doc.uf}`,
      placeId: `ibge:${doc.code}`,
      lat,
      lng,
      ratings: 0,
      googleRating: 0,
      types: ["locality", "administrative_area_level_2"],
      alongKm,
      distanceToRouteKm: projection.distanceKm,
      hits: 1,
      firstAlongKm: alongKm,
      lastAlongKm: alongKm,
      coveredKm: 0,
      cityCode: doc.code,
      routePoint: stopPoint ? { lat: stopPoint.lat, lng: stopPoint.lng } : null,
      routePointDistanceKm: stopPoint ? stopPoint.distanceToCentroidKm : null,
      routePointInsideCity: stopPoint ? stopPoint.insideMunicipality : false,
      ibge: { populacao: population },
      geocoding: {
        isCapital: !!doc.isCapital,
        isMunicipalSeat: doc.isMunicipalSeat !== false,
        stateInitials: doc.uf || null,
      },
    })
    if (candidates.length >= MAX_MUNICIPALITIES) break
  }
  return candidates
}